import type { SlideLibraryCollection, SlideLibrarySlide } from "./types";
import { normalizeText } from "../common/text";

const VALID_COLLECTION_KINDS = new Set(["standard", "preslink_template"]);

function readStringField(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

export function normalizeSlideLibrarySlide(slide: unknown): SlideLibrarySlide | null {
  if (!slide || typeof slide !== "object") {
    return null;
  }
  const record = slide as Record<string, unknown>;
  const slideId = record.slide_id ?? record.slideId;
  if (typeof slideId !== "string" || slideId.trim().length === 0) {
    return null;
  }
  const tags = Array.isArray(record.tags) ? record.tags.filter((tag): tag is string => typeof tag === "string") : [];
  return {
    ...record,
    slide_id: slideId.trim(),
    title: typeof record.title === "string" ? record.title : "",
    description: normalizeText(record.description, 500),
    status: typeof record.status === "string" ? record.status : "UNKNOWN",
    favourite: Boolean(record.favourite),
    tags,
    slide_storage_path:
      readStringField(record, ["slide_storage_path", "slideStoragePath", "slide_path", "slidePath"]) ?? "",
    thumbnail_storage_path:
      readStringField(record, [
        "thumbnail_storage_path",
        "thumbnailStoragePath",
        "thumbnail_path",
        "thumbnailPath",
        "thumbnail_gcs_url",
        "thumbnailGcsUrl",
      ]) ?? "",
    slide_url: readStringField(record, ["slide_url", "slideUrl"]),
    thumbnail_url: readStringField(record, [
      "thumbnail_url",
      "thumbnailUrl",
      "thumbnail_download_url",
      "thumbnailDownloadUrl",
    ]),
    download_url: readStringField(record, ["download_url", "downloadUrl"]),
    collection_id: readStringField(record, ["collection_id", "collectionId"]),
    source: record.source === "company" || record.source === "personal" ? record.source : undefined,
    has_preslink: record.has_preslink === true,
    workbook_ids: Array.isArray(record.workbook_ids)
      ? record.workbook_ids.filter(
          (id): id is string => typeof id === "string" && id.trim().length > 0
        )
      : [],
    created_at:
      typeof record.created_at === "string"
        ? record.created_at
        : typeof record.createdAt === "string"
          ? record.createdAt
          : "",
    updated_at:
      typeof record.updated_at === "string"
        ? record.updated_at
        : typeof record.updatedAt === "string"
          ? record.updatedAt
          : "",
    position:
      typeof record.position === "number" && Number.isFinite(record.position)
        ? record.position
        : undefined,
  };
}

export function normalizeSlideLibraryCollection(collection: unknown): SlideLibraryCollection | null {
  if (!collection || typeof collection !== "object") {
    return null;
  }
  const record = collection as Record<string, unknown>;
  const collectionId = record.collection_id ?? record.collectionId ?? record.id;
  if (typeof collectionId !== "string" || collectionId.trim().length === 0) {
    return null;
  }
  const slideCount = Number(
    record.slide_count ??
      record.slideCount ??
      record.slides_count ??
      record.slidesCount ??
      record.total_slides ??
      record.totalSlides ??
      record.count
  );
  const kind =
    typeof record.kind === "string" && VALID_COLLECTION_KINDS.has(record.kind)
      ? (record.kind as "standard" | "preslink_template")
      : undefined;
  if (
    (record.source === "company" || record.company_id || record.companyId) &&
    typeof record.kind !== "undefined" &&
    !kind
  ) {
    console.warn("[SlideLibrary] Unknown company collection kind; hiding collection", {
      collectionId: collectionId.trim(),
      kind: record.kind,
    });
  }
  return {
    collection_id: collectionId.trim(),
    name: typeof record.name === "string" ? record.name : "Untitled collection",
    description: typeof record.description === "string" ? record.description : "",
    slide_count: Number.isFinite(slideCount) ? slideCount : undefined,
    company_id:
      typeof record.company_id === "string"
        ? record.company_id
        : typeof record.companyId === "string"
          ? record.companyId
          : undefined,
    status: typeof record.status === "string" ? record.status : undefined,
    status_message: readStringField(record, ["status_message"]),
    kind,
    preslink_slide_count:
      typeof record.preslink_slide_count === "number" ? record.preslink_slide_count : undefined,
    source: record.source === "company" || record.source === "personal" ? record.source : undefined,
    readOnly: Boolean(record.readOnly),
    created_at:
      typeof record.created_at === "string"
        ? record.created_at
        : typeof record.createdAt === "string"
          ? record.createdAt
          : "",
    updated_at:
      typeof record.updated_at === "string"
        ? record.updated_at
        : typeof record.updatedAt === "string"
          ? record.updatedAt
          : "",
  };
}
